/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Download, Heart, Settings, Save, Trash2, Bug } from 'lucide-react';
import VideoCard from './components/VideoCard';
import DownloadProgress from './components/DownloadProgress';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import LanguageSwitcher from './components/LanguageSwitcher';
import LanguageInitializer from './components/LanguageInitializer';
import SocialIconsBackground from './components/SocialIconsBackground';
import { ToastContainer, useToast } from './components/Toast';
import { useVideoInfo } from './hooks/useVideoInfo';
import { useDownload } from './hooks/useDownload';
import apiService from './services/api';
import { APP_CONFIG } from './config';
import { NativeDisplayAd, NativeInFeedAd } from './components/AdSenseNative';

function AppContent() {
  const { t, i18n } = useTranslation();
  const [url, setUrl] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('best');
  const [activeTab, setActiveTab] = useState('extract');
  const [serverStatus, setServerStatus] = useState(null);
  const [userCookies, setUserCookies] = useState({});
  const [showCookieManager, setShowCookieManager] = useState(false);
  const [netscapeCookieText, setNetscapeCookieText] = useState('');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Toast 通知
  const { toasts, success, error, hideToast } = useToast();

  // 使用自定义Hooks
  const {
    videoInfo,
    isLoading: isExtracting,
    error: extractError,
    extractVideoInfo,
    clearVideoInfo,
    clearError: clearExtractError,
    getFormatOptions,
    getRecommendedFormat,
    formatFileSize,
    formatDuration
  } = useVideoInfo();

  const {
    tasks,
    isLoading: isDownloading,
    error: downloadError,
    startDownload,
    downloadFile,
    removeTask,
    clearError: clearDownloadError,
    getRunningTasksCount
  } = useDownload();

  // AdSense推送脚本
  const pushAdSenseAds = () => {
    try {
      if (typeof window !== 'undefined' && window.adsbygoogle) {
        const ads = document.querySelectorAll('.adsbygoogle:not([data-adsbygoogle-status])');
        ads.forEach(() => {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        });
      }
    } catch (err) {
      console.error('AdSense error:', err);
    }
  };

  // 检查服务器状态
  useEffect(() => {
    const checkServer = async () => {
      const result = await apiService.checkHealth();
      setServerStatus(result);
    };
    
    checkServer();
    const interval = setInterval(checkServer, 30000);
    return () => clearInterval(interval);
  }, []);

  // 页面加载后推送广告
  useEffect(() => {
    const timer = setTimeout(() => {
      pushAdSenseAds();
    }, 1000);
    return () => clearTimeout(timer);
  }, [activeTab, videoInfo, tasks.length]);

  // 当视频信息提取成功时，设置推荐格式
  useEffect(() => {
    if (videoInfo) {
      const recommended = getRecommendedFormat();
      setSelectedFormat(recommended);
    }
  }, [videoInfo, getRecommendedFormat]);

  // 处理URL提取
  const handleExtract = async () => {
    if (!url.trim()) return;
    clearVideoInfo();
    await extractVideoInfo(url.trim(), userCookies, netscapeCookieText);
  };

  // 处理下载
  const handleDownload = async (mergeAudio = false) => {
    if (!videoInfo || !selectedFormat) {
      error(t('invalidUrl'));
      return;
    }

    try {
      // 确保使用格式ID而不是格式描述
      const formatId = selectedFormat;
      
      const result = await startDownload(videoInfo.url, formatId, 'mp4', { 
        cookies: userCookies, 
        netscapeCookies: netscapeCookieText,
        title: videoInfo.title, // 确保传递视频标题
        mergeAudio: mergeAudio // 添加音频合并选项
      });
      if (result.success) {
        setActiveTab('downloads');
        success(t('success'));
      } else {
        error(`${t('downloadFailed')}: ${result.error}`);
      }
    } catch (err) {
      error(`${t('downloadFailed')}: ${err.message}`);
    }
  };

  // 处理文件下载
  const handleDownloadFile = async (taskId) => {
    const result = await downloadFile(taskId);
    if (result.success) {
      success(t('success'));
    } else {
      error(`${t('downloadFailed')}: ${result.error}`);
    }
  };

  // 处理流式下载
  const handleStreamDownload = async (mergeAudio = false) => {
    if (!videoInfo || !selectedFormat) {
      error(t('invalidUrl'));
      return;
    }

    try {
      // 确保使用格式ID而不是格式描述
      const formatId = selectedFormat;
      
      const result = await apiService.streamDownloadVideo(videoInfo.url, formatId, { 
        cookies: userCookies, 
        netscapeCookies: netscapeCookieText,
        fileExt: "mp4",
        mergeAudio: mergeAudio,
        title: videoInfo.title // 确保传递视频标题
      });
      if (result.success) {
        success(t('streamStarted'));
      } else {
        error(`${t('streamFailed')}: ${result.error}`);
      }
    } catch (err) {
      error(`${t('streamFailed')}: ${err.message}`);
    }
  };

  const activeTasksCount = getRunningTasksCount();

  // Cookie管理功能
  const detectPlatformFromUrl = (url) => {
    try {
      const hostname = new URL(url).hostname.toLowerCase();
      if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
        return 'youtube';
      } else if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
        return 'twitter';
      } else if (hostname.includes('bilibili.com')) {
        return 'bilibili';
      } else if (hostname.includes('tiktok.com')) {
        return 'tiktok';
      } else if (hostname.includes('instagram.com')) {
        return 'instagram';
      }
      return 'unknown';
    } catch {
      return 'unknown';
    }
  };

  const handleSaveCookies = () => {
    try {
      const cookies = {};
      
      // 解析Netscape格式的cookies
      if (netscapeCookieText.trim()) {
        // 按行分割
        const lines = netscapeCookieText.split('\n');
        // 过滤掉注释行和空行
        const cookieLines = lines.filter(line => 
          line.trim() && !line.trim().startsWith('#')
        );
        
        // 处理每行cookie
        cookieLines.forEach(line => {
          const parts = line.split('\t');
          // Netscape格式应该有至少7个部分
          if (parts.length >= 7) {
            const name = parts[5].trim();
            const value = parts[6].trim();
            if (name && value) {
              cookies[name] = value;
            }
          }
        });
      }

      const storageKey = 'streammate_cookies';
      localStorage.setItem(storageKey, JSON.stringify(cookies));
      setUserCookies(cookies);
      success(`${t('success')} ${Object.keys(cookies).length} cookies`);
    } catch (err) {
      error(`${t('error')}: ${err.message}`);
    }
  };

  const handleClearCookies = () => {
    const storageKey = 'streammate_cookies';
    localStorage.removeItem(storageKey);
    setUserCookies({});
    setNetscapeCookieText('');
    success(t('success'));
  };

  // 当组件加载时，尝试加载保存的cookies
  useEffect(() => {
    try {
      const storageKey = 'streammate_cookies';
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const cookies = JSON.parse(stored);
        setUserCookies(cookies);
      }
    } catch (error) {
      console.warn('加载Cookie失败:', error);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 social-icons-background flex flex-col">
      <SocialIconsBackground />

      {/* 头部 */}
      <header className="bg-white shadow-sm border-b border-gray-200 content-above-icons">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/favicon/android-chrome-192x192.png" 
                alt="StreamMate Logo" 
                className="w-10 h-10 rounded-lg"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">StreamMate - Video download tool</h1>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* App Store链接 */}
              <a 
                href="https://apps.apple.com/us/app/streammate-x/id6745404700" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-colors"
                title="Download on App Store"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.45 13.62c-.03-2.5 2.14-3.74 2.23-3.78-1.21-1.77-3.1-2.02-3.76-2.05-1.6-.16-3.12.94-3.93.94-.8 0-2.05-.92-3.36-.89-1.73.02-3.32 1-4.2 2.54-1.8 3.11-.46 7.7 1.28 10.23.85 1.24 1.87 2.62 3.2 2.57 1.27-.05 1.76-.82 3.3-.82 1.53 0 1.96.82 3.3.8 1.37-.03 2.24-1.25 3.07-2.5.97-1.42 1.37-2.8 1.4-2.86-.03-.01-2.7-1.03-2.72-4.08M14.12 6.48c.7-.84 1.17-2.01 1.04-3.18-1 .04-2.2.67-2.92 1.5-.64.74-1.2 1.92-1.05 3.06 1.12.08 2.26-.54 2.93-1.38" />
                </svg>
                <span className="hidden sm:inline">{t('appStore')}</span>
              </a>
              
              <div className="relative">
                <button 
                  onClick={() => setShowLanguageDropdown(!showLanguageDropdown)} 
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" 
                  aria-label="Select language"
                >
                  <span className="text-lg">
                    {i18n.language === 'en' ? '🇺🇸' : 
                     i18n.language === 'zh' ? '🇨🇳' : 
                     i18n.language === 'ja' ? '🇯🇵' :
                     i18n.language === 'ko' ? '🇰🇷' :
                     i18n.language === 'es' ? '🇪🇸' :
                     i18n.language === 'fr' ? '🇫🇷' :
                     i18n.language === 'de' ? '🇩🇪' :
                     i18n.language === 'ru' ? '🇷🇺' : '🇺🇸'}
                  </span>
                  <span className="hidden sm:inline">
                    {i18n.language === 'en' ? 'English' : 
                     i18n.language === 'zh' ? '中文' : 
                     i18n.language === 'ja' ? '日本語' :
                     i18n.language === 'ko' ? '한국어' :
                     i18n.language === 'es' ? 'Español' :
                     i18n.language === 'fr' ? 'Français' :
                     i18n.language === 'de' ? 'Deutsch' :
                     i18n.language === 'ru' ? 'Русский' : 'English'}
                  </span>
                  <svg 
                    className={`w-4 h-4 transition-transform ${showLanguageDropdown ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                
                {showLanguageDropdown && (
                  <>
                    {/* 背景遮罩，点击关闭下拉菜单 */}
                    <div 
                      className="fixed inset-0 z-[9998]" 
                      onClick={() => setShowLanguageDropdown(false)}
                    />
                    
                    {/* 下拉菜单 */}
                    <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] max-h-80 overflow-y-auto">
                      <div className="py-1">
                        {/* 语言选项 */}
                        <button
                          onClick={() => {
                            i18n.changeLanguage('zh');
                            setShowLanguageDropdown(false);
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${i18n.language === 'zh' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          <span className="text-lg">🇨🇳</span>
                          <span className="flex-1 text-left">中文</span>
                          {i18n.language === 'zh' && (
                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            i18n.changeLanguage('en');
                            setShowLanguageDropdown(false);
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          <span className="text-lg">🇺🇸</span>
                          <span className="flex-1 text-left">English</span>
                          {i18n.language === 'en' && (
                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            i18n.changeLanguage('ja');
                            setShowLanguageDropdown(false);
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${i18n.language === 'ja' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          <span className="text-lg">🇯🇵</span>
                          <span className="flex-1 text-left">日本語</span>
                          {i18n.language === 'ja' && (
                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            i18n.changeLanguage('ko');
                            setShowLanguageDropdown(false);
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${i18n.language === 'ko' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          <span className="text-lg">🇰🇷</span>
                          <span className="flex-1 text-left">한국어</span>
                          {i18n.language === 'ko' && (
                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            i18n.changeLanguage('es');
                            setShowLanguageDropdown(false);
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${i18n.language === 'es' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          <span className="text-lg">🇪🇸</span>
                          <span className="flex-1 text-left">Español</span>
                          {i18n.language === 'es' && (
                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            i18n.changeLanguage('fr');
                            setShowLanguageDropdown(false);
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${i18n.language === 'fr' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          <span className="text-lg">🇫🇷</span>
                          <span className="flex-1 text-left">Français</span>
                          {i18n.language === 'fr' && (
                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            i18n.changeLanguage('de');
                            setShowLanguageDropdown(false);
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${i18n.language === 'de' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          <span className="text-lg">🇩🇪</span>
                          <span className="flex-1 text-left">Deutsch</span>
                          {i18n.language === 'de' && (
                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            i18n.changeLanguage('ru');
                            setShowLanguageDropdown(false);
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${i18n.language === 'ru' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                        >
                          <span className="text-lg">🇷🇺</span>
                          <span className="flex-1 text-left">Русский</span>
                          {i18n.language === 'ru' && (
                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  serverStatus?.success ? 'bg-green-500' : 'bg-red-500'
                }`} />
                <span className="text-sm text-gray-600">
                  {serverStatus?.success ? t('serviceNormal') : t('serviceError')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* 头部广告 - Google AdSense原生代码 */}
      <ins className="adsbygoogle"
           style={{display: 'block', textAlign: 'center'}}
           data-ad-client="ca-pub-9015098740645371"
           data-ad-slot="8612722225"
           data-ad-format="auto"
           data-full-width-responsive="true"></ins>

      {/* 主要内容 */}
      <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 content-above-icons">
        {/* 标签页导航 */}
        <div className="flex items-center gap-1 mb-8 bg-gray-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('extract')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'extract'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Search className="w-4 h-4 inline mr-2" />
            {t('downloadVideo')}
          </button>
          
          <button
            onClick={() => setActiveTab('downloads')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all relative ${
              activeTab === 'downloads'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Download className="w-4 h-4 inline mr-2" />
            {t('downloading')}
            {activeTasksCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {activeTasksCount}
              </span>
            )}
          </button>
        </div>

        {/* 视频提取标签页 */}
        {activeTab === 'extract' && (
          <div className="space-y-8">
            {/* URL输入区域 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('downloadVideo')}</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('pasteUrl')}
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder={t('urlPlaceholder')}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      onKeyPress={(e) => e.key === 'Enter' && handleExtract()}
                    />
                    <button
                      onClick={handleExtract}
                      disabled={isExtracting || !url.trim()}
                      className={`px-6 py-3 rounded-lg font-medium transition-all ${
                        isExtracting || !url.trim()
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800'
                      } text-white shadow-lg hover:shadow-xl`}
                    >
                      {isExtracting ? t('downloading') : t('favorite')}
                    </button>
                  </div>
                  
                  {/* Cookie管理器 */}
                  <div className="mt-4">
                    <button
                      onClick={() => setShowCookieManager(!showCookieManager)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                      title={t('cookieSettings')}
                    >
                      <Settings className="w-4 h-4" />
                      <span>{t('cookieSettings')}</span>
                      {Object.keys(userCookies).length > 0 && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                          {Object.keys(userCookies).length}
                        </span>
                      )}
                    </button>

                    {showCookieManager && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-medium text-gray-900">
                            {t('cookieSettings')}
                          </h3>
                          <button
                            onClick={() => setShowCookieManager(false)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Cookie使用说明 */}
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <h4 className="text-sm font-medium text-blue-800 mb-2">{t('whenToUseCookies')}</h4>
                          <ul className="text-xs text-blue-700 space-y-1 list-disc pl-4">
                            <li>{t('cookieUseCase1')}</li>
                            <li>{t('cookieUseCase2')}</li>
                            <li>{t('cookieUseCase3')}</li>
                            <li>{t('cookieUseCase4')}</li>
                          </ul>
                          
                          <h4 className="text-sm font-medium text-blue-800 mt-3 mb-2">{t('howToGetCookies')}</h4>
                          <div className="text-xs text-blue-700">
                            <p className="mb-1">{t('cookieExtensionGuide')}</p>
                            <ol className="list-decimal pl-4 space-y-1">
                              <li>{t('cookieExtensionStep1')}</li>
                              <li>{t('cookieExtensionStep2')}</li>
                              <li>{t('cookieExtensionStep3')}</li>
                            </ol>
                            <p className="mt-2 font-medium text-blue-800">{t('netscapeCookieRequired')}</p>
                          </div>
                        </div>

                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('netscapeCookie')}
                          </label>
                          <textarea
                            value={netscapeCookieText}
                            onChange={(e) => setNetscapeCookieText(e.target.value)}
                            placeholder="# Netscape HTTP Cookie File\n# http://curl.haxx.se/rfc/cookie_spec.html\n# This is a generated file!  Do not edit.\n\n.youtube.com\tTRUE\t/\tTRUE\t1783330720\tLOGIN_INFO\tAFmmF2swRQIge6eDP72FbFFafdMrkMtRfBs4aYurzYs70UrwuZhhvbcCIQDJ6duVCAaWth1hRmY3skoQ2etrWvBSovH1swfbHsiQhw:QUQ3MjNm..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            rows={6}
                            style={{ fontFamily: 'monospace' }}
                          />
                        </div>

                        {Object.keys(userCookies).length > 0 && (
                          <div className="mb-3 p-2 bg-green-50 rounded text-sm text-green-700">
                            ✓ {t('success')} {Object.keys(userCookies).length} cookies
                          </div>
                        )}

                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveCookies}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                          >
                            <Save className="w-4 h-4" />
                            {t('save')}
                          </button>
                          
                          {Object.keys(userCookies).length > 0 && (
                            <button
                              onClick={handleClearCookies}
                              className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t('clear')}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 错误信息 */}
            {extractError && (
              <ErrorMessage
                message={extractError}
                onClose={clearExtractError}
                type="error"
              />
            )}

            {/* 加载状态 */}
            {isExtracting && (
              <LoadingSpinner message={t('downloading')} size="lg" />
            )}

            {/* 视频信息卡片 */}
            {videoInfo && (
              <>
                <VideoCard
                  videoInfo={videoInfo}
                  selectedFormat={selectedFormat}
                  onFormatChange={setSelectedFormat}
                  formatOptions={getFormatOptions()}
                  onDownload={handleDownload}
                  onStreamDownload={handleStreamDownload}
                  isDownloading={isDownloading}
                />
              </>
            )}
          </div>
        )}

        {/* 下载任务标签页 */}
        {activeTab === 'downloads' && (
          <div>
            {downloadError && (
              <div className="mb-6">
                <ErrorMessage
                  message={downloadError}
                  onClose={clearDownloadError}
                  type="error"
                />
              </div>
            )}

            <DownloadProgress
              tasks={tasks}
              onDownloadFile={handleDownloadFile}
              onRemoveTask={removeTask}
              formatFileSize={formatFileSize}
              formatDuration={formatDuration}
            />
          </div>
        )}
      </main>

      <ToastContainer toasts={toasts} onClose={hideToast} />

      {/* 页脚 */}
      <footer className="bg-white border-t border-gray-200 mt-auto content-above-icons">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-500">
              © 2025 StreamMate.
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Made with</span>
              <Heart className="w-4 h-4 text-red-500" />
              <span>by StreamMate Team</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// 主App组件，使用LanguageInitializer包装
function App() {
  return (
    <LanguageInitializer>
      <AppContent />
    </LanguageInitializer>
  );
}

export default App; 